import { Box, Button, ButtonGroup, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { ResultTabNavigatorExtraProps } from '../types/reports';
import { useThemeContext } from '../contexts/BaseThemeContextProvider';

const ResultTabNavigator = ({ activeView, setActiveView, allResults }: ResultTabNavigatorExtraProps) => {
  const { theme } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const buttonGroupStyles = {
    borderRadius: '12px',
    overflow: 'hidden',
    '& .MuiButton-root': {
      flex: 1,
      backgroundColor: theme.custom.report.details.resultTabNavigator.button.root.backgroundColor,
      color: theme.custom.report.details.resultTabNavigator.button.root.color,
      textTransform: 'none',
      fontWeight: 'bold',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '12px 12px',
      border: `1px solid ${theme.custom.report.details.resultTabNavigator.button.root.borderColor}`,
      minWidth: '0',
      '& img': {
        width: '18px',
        height: '18px',
      },
    },
    '& .MuiButton-root.Mui-selected': {
      backgroundColor: theme.custom.report.details.resultTabNavigator.button.selected.backgroundColor,
      color: theme.custom.report.details.resultTabNavigator.button.selected.color,
      border: `1px solid ${theme.custom.report.details.resultTabNavigator.button.selected.borderColor}`,
      '&:hover': {
        backgroundColor: theme.custom.report.details.resultTabNavigator.button.selected.onHover.backgroundColor,
      },
    },
    '& .MuiButton-root:first-of-type': {
      borderTopLeftRadius: '12px',
      borderBottomLeftRadius: '12px',
      paddingLeft: '16px',
    },
    '& .MuiButton-root:last-of-type': {
      borderTopRightRadius: '12px',
      borderBottomRightRadius: '12px',
    },
    '& .MuiButton-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  };

  const tabTitleStyles = {
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: activeView === 'comparison' ? '500' : '400',
    lineHeight: '16px',
    margin: '0px !important',
    padding: '0px !important',
  };

  const renderTimelineBtn = (allResults: any[]) => {
    const isDisabled = allResults.length <= 1;

    return (
      !isMobile && (
        <Tooltip
          arrow
          placement="top"
          title={isDisabled ? 'The Timeline view is only enabled once you have more than one test.' : ''}
          enterTouchDelay={0}
          leaveTouchDelay={5000}
          componentsProps={{
            tooltip: {
              sx: {
                color: theme.custom.report.details.resultTabNavigator.tooltip.color,
                fontSize: '12px',
                fontWeight: 600,
                backgroundColor: theme.custom.report.details.resultTabNavigator.tooltip.backgroundColor,
                boxShadow: theme.custom.report.details.resultTabNavigator.tooltip.boxShadow,
                padding: '16px',
                '& .MuiTooltip-arrow': {
                  fontSize: '12px',
                  color: theme.custom.report.details.resultTabNavigator.tooltip.arrow.color,
                },
                borderRadius: '8px',
              },
            },
          }}
        >
          <Button
            sx={{
              width: '130px',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.5 : 1,
            }}
            startIcon={
              <img
                src={
                  activeView === 'timeline'
                    ? theme.custom.report.details.resultTabNavigator.images.timeline.selected.src
                    : theme.custom.report.details.resultTabNavigator.images.timeline.root.src
                }
                alt={'timeline view'}
              />
            }
            onClick={() => !isDisabled && setActiveView('timeline')}
            className={activeView === 'timeline' ? 'Mui-selected' : ''}
          >
            <Typography sx={tabTitleStyles}>Timeline</Typography>
          </Button>
        </Tooltip>
      )
    );
  };

  return (
    <Box>
      <ButtonGroup variant="contained" sx={buttonGroupStyles}>
        <Button
          startIcon={
            <img
              src={
                activeView === 'comparison'
                  ? theme.custom.report.details.resultTabNavigator.images.comparison.selected.src
                  : theme.custom.report.details.resultTabNavigator.images.comparison.root.src
              }
              alt={'comparison view'}
            />
          }
          onClick={() => setActiveView('comparison')}
          className={activeView === 'comparison' ? 'Mui-selected' : ''}
        >
          <Typography sx={tabTitleStyles}>Comparison</Typography>
        </Button>
        <Button
          startIcon={
            <img
              src={
                activeView === 'card'
                  ? theme.custom.report.details.resultTabNavigator.images.card.selected.src
                  : theme.custom.report.details.resultTabNavigator.images.card.root.src
              }
              alt={'card view'}
            />
          }
          onClick={() => setActiveView('card')}
          className={activeView === 'card' ? 'Mui-selected' : ''}
        >
          <Typography sx={tabTitleStyles}>Card View</Typography>
        </Button>

        {!isMobile && (
          <Button
            startIcon={
              <img
                src={
                  activeView === 'table'
                    ? theme.custom.report.details.resultTabNavigator.images.table.selected.src
                    : theme.custom.report.details.resultTabNavigator.images.table.root.src
                }
                alt={'table view'}
              />
            }
            onClick={() => setActiveView('table')}
            className={activeView === 'table' ? 'Mui-selected' : ''}
          >
            <Typography sx={tabTitleStyles}>Table View</Typography>
          </Button>
        )}
        {!isMobile && (
          <Button
            sx={{ width: '134px' }}
            startIcon={
              <img
                src={
                  activeView === 'bubble'
                    ? theme.custom.report.details.resultTabNavigator.images.bubble.selected.src
                    : theme.custom.report.details.resultTabNavigator.images.bubble.root.src
                }
                alt={'bubble view'}
              />
            }
            onClick={() => setActiveView('bubble')}
            className={activeView === 'bubble' ? 'Mui-selected' : ''}
          >
            <Typography sx={tabTitleStyles}>Bubble view</Typography>
          </Button>
        )}
        {renderTimelineBtn(allResults)}
      </ButtonGroup>
    </Box>
  );
};

export default ResultTabNavigator;
